import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { useClasses } from "@xo-union/react-css-modules";
import classesPropType from "@xo-union/classes-prop-type";
import Icon, { IconButton } from "@xo-union/tk-component-icons";
import { Button, Link } from "@xo-union/tk-component-buttons";
import { useUniqueID } from "@xo-union/react-unique-id";
import styles from "./styles.css";
import ScreenReaderPrefix from "../ScreenReaderPrefix.js";
const defaultIconNames = {
  error: 'error',
  info: 'info',
  success: 'checkmark_circle',
  warning: 'warning'
};

const defaultIconRender = props => /*#__PURE__*/React.createElement(Icon, props);

export const InlineAlertCtaButton = props => /*#__PURE__*/React.createElement(Button, _extends({}, props, {
  color: "secondary",
  size: "md"
}));
export const InlineAlertCtaLink = _ref => {
  let {
    children,
    ...props
  } = _ref;
  return /*#__PURE__*/React.createElement(Link, _extends({}, props, {
    color: "secondary",
    size: "md"
  }), children);
};
export const InlineAlert = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    'aria-live': ariaLive,
    children,
    classes: classesProp,
    className,
    cta,
    headline,
    headlineId,
    icon,
    id,
    onCloseButtonClick,
    role: givenRole,
    type,
    visible
  } = _ref2;
  const classes = useClasses(styles, classesProp);
  const visibility = visible ? 'is-visible' : 'is-not-visible';
  const visibleClass = classes[visibility] || '';
  const role = visible ? givenRole : 'none';
  const live = visible && role !== 'none' ? ariaLive : 'off';
  const ariaHidden = !visible;
  const visibleCloseButton = onCloseButtonClick !== undefined && type !== 'error';
  const defaultIconName = defaultIconNames[type];
  const iconProps = {
    'aria-hidden': 'true',
    className: cx(classes['alert-icon'], classes[`icon-${type}`]),
    size: 'md'
  };
  const uniqueHeadlineId = useUniqueID(headlineId);
  return /*#__PURE__*/React.createElement("div", {
    className: cx(classes.container, classes[type], className, visibleClass),
    role: "group",
    "aria-labelledby": uniqueHeadlineId,
    "aria-hidden": ariaHidden,
    "aria-roledescription": "alert",
    ref: ref,
    id: id
  }, icon !== false && (typeof icon === 'function' ? icon({ ...iconProps,
    name: defaultIconName
  }) : defaultIconRender({ ...iconProps,
    name: icon || defaultIconName
  })), /*#__PURE__*/React.createElement("div", {
    className: classes['text-container'],
    role: role,
    "aria-live": live
  }, headline ? /*#__PURE__*/React.createElement("div", {
    role: "heading",
    "aria-level": "1",
    className: classes['alert-headline'],
    id: uniqueHeadlineId
  }, /*#__PURE__*/React.createElement(ScreenReaderPrefix, {
    type: type
  }), headline) : /*#__PURE__*/React.createElement(ScreenReaderPrefix, {
    id: uniqueHeadlineId,
    type: type
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.children
  }, children), cta && /*#__PURE__*/React.createElement("div", {
    className: classes['alert-cta-container']
  }, cta)), visibleCloseButton && /*#__PURE__*/React.createElement("div", {
    className: classes['close-button-container']
  }, /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": "close",
    className: classes['close-button'],
    name: "close",
    onClick: onCloseButtonClick,
    size: "md"
  })));
});
process.env.NODE_ENV !== "production" ? InlineAlertCtaLink.propTypes = {
  /**
   * Contents of CTA Link
   */
  children: PropTypes.node.isRequired
} : void 0;
process.env.NODE_ENV !== "production" ? InlineAlert.propTypes = {
  /**
   * The aria-live setting for the component
   */
  'aria-live': PropTypes.string,

  /**
   * Contents of alert
   */
  children: PropTypes.node.isRequired,

  /**
   * Class dependencies
   */
  classes: classesPropType(['container', 'children', 'alert-icon', 'text-container']),

  /**
   * Additional class to append to inline alert container
   */
  className: PropTypes.string,

  /**
   * CTA; pass in either an `InlineAlertCtaButton` or `InlineAlertCtaLink`, which accept all of the props that `@xo-union/tk-component-buttons`'s `Button` and `Link` components accept, respectively.
   */
  cta: PropTypes.node,

  /**
   * Optional headline for the alert
   */
  headline: PropTypes.string,

  /**
   * id of the alert headline (useful if you see warnings about duplicate ids)
   */
  headlineId: PropTypes.string,

  /**
   * Use a custom icon name from tk-component-icons instead of the built in (info, warning, error) icons
   *
   * You can omit the icon by passing `false`
   * You can also use a render function to override the rendering all together
   */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.oneOf([false])]),

  /**
   * ID of the alert component
   */
  id: PropTypes.string,

  /**
   * Adds an "X"/close button to the alert and gets called when the button is clicked. Note: this prop does nothing when `type="error` because close buttons are disallowed for "error" alerts
   */
  onCloseButtonClick: PropTypes.func,

  /**
   * The aria role for the component. Setting role="none" makes it so screen readers do not immediately read the alert after page load since this alert is static
   */
  role: PropTypes.string,

  /**
   * Select the type of alert to display
   */
  type: PropTypes.oneOf(['error', 'info', 'success', 'warning']).isRequired,

  /**
   * Should the alert be visible
   */
  visible: PropTypes.bool
} : void 0;
InlineAlert.defaultProps = {
  'aria-live': 'polite',
  role: 'alert',
  visible: true
};